import { useEffect, useState } from 'react'
import Pagination from '../components/commons/Pagination'
import { getAllBoxTypes, updateBoxType } from '../firebase/boxTypes'
import { formatDate } from '../utils'
import { useAtom } from 'jotai'
import { showCreateBoxTypeModalAtom, showManageBoxTypeModalAtom } from '../store'
import DialogCreateBoxType from '../components/dialogs/DialogCreateBoxType'

const BoxTypeItem = ({ boxType, handleToggleState, loadingStates }) => {
    const isLoading = loadingStates[boxType.id] || false

    return (
        <tr key={boxType.id} className='h-[70px] hover:bg-[#2F2F2F] p-2'>
            <td>
                {boxType.label}
            </td>

            <td>
                {boxType.value}
            </td>

            <td>
                {boxType.created_at ? formatDate(boxType.created_at.seconds) : 'N/A'}
            </td>

            <td>
                <div className='flex justify-end pr-10'>
                    <button
                        className={`px-6 py-2 rounded-[33px] flex items-center ${
                            boxType.state === 'active' 
                                ? 'bg-green-500 text-white' 
                                : 'bg-yellow-500 text-white'
                        } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        onClick={() => !isLoading && handleToggleState(boxType)}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <img src="/assets/icons/icon-loading.svg" alt="loading" className="w-4 h-4 animate-spin mr-2" />
                                Updating...
                            </>
                        ) : (
                            boxType.state === 'active' ? 'Active' : 'Pending'
                        )}
                    </button>
                </div>
            </td>
        </tr>
    )
}

const BoxTypeManagement = () => {
    const [currentPage, setCurrentPage] = useState(1)
    const [boxTypes, setBoxTypes] = useState(null)
    const [loading, setLoading] = useState(true)
    const [updatedTimes, setUpdatedTimes] = useState(0)
    const [showCreateBoxTypeModal, setShowCreateBoxTypeModal] = useAtom(showCreateBoxTypeModalAtom)
    const [showManageBoxTypeModal, setShowManageBoxTypeModal] = useAtom(showManageBoxTypeModalAtom)
    const [selectedBoxType, setSelectedBoxType] = useState(null)
    const [loadingStates, setLoadingStates] = useState({})
    const fields = ["Label", 'Value', 'Date added', 'Status']

    useEffect(() => {
        updateBoxTypes()
    }, [showCreateBoxTypeModal, showManageBoxTypeModal])

    const updateBoxTypes = () => {
        getAllBoxTypes(["active", "pending"]).then((types) => {
            setBoxTypes(types)
            setLoading(false)
        })

        setUpdatedTimes(updatedTimes + 1)
    }

    const handleToggleState = async (boxType) => {
        // Set loading state for this specific box type
        setLoadingStates(prev => ({ ...prev, [boxType.id]: true }))
        
        try {
            const newState = boxType.state === 'active' ? 'pending' : 'active'
            await updateBoxType({
                id: boxType.id,
                label: boxType.label,
                value: boxType.value,
                state: newState
            })
            updateBoxTypes() // Refresh the data
        } catch (error) {
            console.error('Error updating box type state:', error)
        } finally {
            // Clear loading state for this specific box type
            setLoadingStates(prev => ({ ...prev, [boxType.id]: false }))
        }
    }

    return (
        <>
            <DialogCreateBoxType
                open={showCreateBoxTypeModal}
                onClose={() => setShowCreateBoxTypeModal(false)}
            />

            <div className="px-10 py-8">
                <div className="flex items-center justify-between">
                    <h1 className="text-primary text-[36px]">Box Type Manager</h1>

                    <button className="px-6 py-2 bg-white rounded-[33px]" onClick={() => setShowCreateBoxTypeModal(true)}>Add New</button>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="flex flex-col items-center">
                            <img src="/assets/icons/icon-loading.svg" alt="loading" className="w-8 h-8 animate-spin mb-4" />
                            <p className="text-white text-lg">Loading box types...</p>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="my-10">
                            <div className="w-full overflow-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="bg-[#2E2E2E] rounded-[4px] py-3 text-sm text-[#717171]">
                                            {
                                                fields.map((item, index) =>
                                                    <th className={`py-3 text-left ${index > 0 ? '' : 'pl-3'}`} key={`boxType-field-${item}-${index}`}>
                                                        {item}
                                                    </th>
                                                )
                                            }
                                        </tr>
                                    </thead>

                                    <tbody className="text-white space-y-1 text-sm">
                                        {
                                            boxTypes.slice(8 * (currentPage - 1), 8 * currentPage).map(boxType =>
                                                <BoxTypeItem key={boxType.id} boxType={boxType} handleToggleState={handleToggleState} loadingStates={loadingStates} />
                                            )
                                        }
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="mt-auto">
                            <Pagination itemsPerPage={8} totalItems={boxTypes.length} onPageChange={(v) => { setCurrentPage(v) }} />
                        </div>
                    </>
                )}
            </div>
        </>
    )
}

export default BoxTypeManagement 